import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatIDR, formatDateID, todayISO } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Trash2, Wallet } from "lucide-react";
import CurrencyInput from "@/components/CurrencyInput";

const typeLabel = {
  initial: "Modal Awal",
  add: "Tambah Modal",
  withdraw: "Tarik Modal",
};

export default function Capital() {
  const [items, setItems] = useState([]);
  const [balance, setBalance] = useState(0);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: "add", amount: 0, date: todayISO(), note: "" });

  const load = async () => {
    const r = await api.get("/reports/capital");
    setItems(r.data.items);
    setBalance(r.data.balance);
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/capital", {
        type: form.type,
        amount: Number(form.amount) || 0,
        date: form.date,
        note: form.note,
      });
      toast.success("Modal dicatat");
      setOpen(false);
      setForm({ type: "add", amount: 0, date: todayISO(), note: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan");
    }
  };

  const remove = async (c) => {
    if (!window.confirm("Hapus catatan modal ini?")) return;
    try {
      await api.delete(`/capital/${c.id}`);
      toast.success("Dihapus");
      load();
    } catch (err) {
      toast.error("Gagal menghapus");
    }
  };

  return (
    <div className="space-y-6" data-testid="capital-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-zinc-900">Manajemen Modal</h1>
          <p className="text-sm text-zinc-500 mt-1">Catat modal awal, tambahan, dan penarikan.</p>
        </div>
        <Button data-testid="btn-add-capital" onClick={() => setOpen(true)} className="bg-zinc-900 hover:bg-zinc-800 rounded-md">
          <Plus className="h-4 w-4 mr-1.5" />
          Catat Modal
        </Button>
      </div>

      <Card className="p-6 border-zinc-200 shadow-none rounded-md bg-gradient-to-br from-white to-zinc-50">
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 rounded-md bg-zinc-900 text-white flex items-center justify-center">
            <Wallet className="h-5 w-5" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-zinc-500">Saldo Modal Berjalan</div>
            <div className="text-3xl font-display font-bold num text-zinc-900" data-testid="capital-balance">
              {formatIDR(balance)}
            </div>
          </div>
        </div>
      </Card>

      <Card className="border-zinc-200 shadow-none rounded-md">
        <div className="p-4 border-b border-zinc-200 text-sm font-semibold">Riwayat Perubahan Modal</div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-zinc-50">
              <TableRow>
                <TableHead className="w-[110px]">Tanggal</TableHead>
                <TableHead>Tipe</TableHead>
                <TableHead>Catatan</TableHead>
                <TableHead className="text-right">Jumlah</TableHead>
                <TableHead className="text-right">Saldo</TableHead>
                <TableHead className="w-[60px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-testid="capital-table-body">
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-zinc-500">
                    Belum ada catatan modal.
                  </TableCell>
                </TableRow>
              )}
              {items.map((c) => (
                <TableRow key={c.id} className="hover:bg-zinc-50 transition-colors duration-150">
                  <TableCell className="whitespace-nowrap text-zinc-600">{formatDateID(c.date)}</TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={
                        c.type === "withdraw"
                          ? "border-rose-200 bg-rose-50 text-rose-700"
                          : "border-emerald-200 bg-emerald-50 text-emerald-700"
                      }
                    >
                      {typeLabel[c.type]}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-zinc-600">{c.note}</TableCell>
                  <TableCell className={`text-right num font-semibold ${c.type === "withdraw" ? "text-rose-700" : "text-emerald-700"}`}>
                    {c.type === "withdraw" ? "-" : "+"} {formatIDR(c.amount)}
                  </TableCell>
                  <TableCell className="text-right num text-zinc-600">{formatIDR(c.running_balance)}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      data-testid={`btn-del-capital-${c.id}`}
                      size="icon"
                      variant="ghost"
                      onClick={() => remove(c)}
                      className="h-8 w-8 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="capital-dialog">
          <DialogHeader>
            <DialogTitle>Catat Modal</DialogTitle>
            <DialogDescription>Pilih tipe (awal / tambah / tarik) dan isi jumlah.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Tipe</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="select-capital-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="initial">Modal Awal</SelectItem>
                  <SelectItem value="add">Tambah Modal</SelectItem>
                  <SelectItem value="withdraw">Tarik Modal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-1">
              <Label>Tanggal</Label>
              <Input
                data-testid="input-capital-date"
                type="date"
                required
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
              />
            </div>
            <div className="col-span-1">
              <Label>Jumlah (Rp)</Label>
              <CurrencyInput
                data-testid="input-capital-amount"
                value={form.amount}
                onChange={(v) => setForm({ ...form, amount: v })}
              />
            </div>
            <div className="col-span-2">
              <Label>Catatan</Label>
              <Input
                data-testid="input-capital-note"
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
              />
            </div>
            <DialogFooter className="col-span-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Batal</Button>
              <Button data-testid="btn-save-capital" type="submit" className="bg-zinc-900 hover:bg-zinc-800">
                Simpan
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
